import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle, CheckCircle2, Clock, Loader2, Lock, MapPin, RefreshCw, Truck, X } from 'lucide-react'
import { useApp } from '../App'
import { platedAPI } from '../lib/api'
import { useToastActions } from '../components/Toast'
import { HOLD_LABEL, PLATED_CITIES, approvalEffects, declineEffects, dishRows, importEffects, waitingOrders } from '../lib/plated'

const OPERATOR_KEY = 'havn_plated_operator'
const readOperator = () => { try { return localStorage.getItem(OPERATOR_KEY) || '' } catch { return '' } }
const saveOperator = (name) => { try { localStorage.setItem(OPERATOR_KEY, name) } catch { /* private window */ } }
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '')
const REFRESHING = new Set(['queued', 'waiting', 'importing'])

/**
 * The update-permission pop-up: lists what the change does downstream, asks who is confirming,
 * and (for a re-lock or a decline) why. Radix Dialog: portaled, focus-trapped, Escape closes.
 */
function UpdateDialog({ request, busy, returnFocus, onClose, onConfirm }) {
  const [name, setName] = useState(readOperator)
  const [reason, setReason] = useState('')
  useEffect(() => { if (request) { setReason(''); setName(readOperator()) } }, [request])
  const needsReason = request?.reason === 'required'
  const ready = name.trim() && (!needsReason || reason.trim())

  return (
    <Dialog.Root open={Boolean(request)} onOpenChange={(next) => { if (!next && !busy) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[150] bg-havn-green-900/60 backdrop-blur-sm animate-fade-in" />
        <Dialog.Content
          onCloseAutoFocus={(e) => { const el = returnFocus?.current; if (el?.isConnected) { e.preventDefault(); el.focus() } }}
          className="fixed left-1/2 top-1/2 z-[150] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 max-h-[calc(100vh-2rem)] overflow-y-auto rounded-2xl bg-white shadow-2xl outline-none">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (!ready || busy) return
              saveOperator(name.trim())
              onConfirm({ operator: name.trim(), reason: reason.trim() })
            }}
          >
            <div className="flex items-start gap-3 px-5 pt-5">
              <div className="min-w-0 flex-1">
                <Dialog.Title className="font-display text-xl text-havn-green-900">{request?.title}</Dialog.Title>
                <Dialog.Description className="mt-1 text-sm text-havn-gray-500">{request?.description}</Dialog.Description>
              </div>
              <Dialog.Close asChild>
                <button type="button" className="btn-icon-sm -mr-2 -mt-1" aria-label="Close" disabled={busy}><X className="w-4 h-4" /></button>
              </Dialog.Close>
            </div>

            {request?.rows?.length > 0 && (
              <dl className="mx-5 mt-4 divide-y divide-havn-cream-200 rounded-lg border border-havn-cream-300">
                {request.rows.map((row) => (
                  <div key={row.area} className="grid grid-cols-[7.5rem_1fr] gap-3 px-3 py-2 text-sm">
                    <dt className="font-medium text-havn-charcoal">{row.area}</dt>
                    <dd className="text-havn-gray-600">{row.detail}</dd>
                  </div>
                ))}
              </dl>
            )}

            <div className="space-y-3 px-5 pt-4">
              {request?.reason && (
                <label className="block text-sm">
                  <span className="font-medium text-havn-charcoal">{request.reasonLabel || 'Reason'}{needsReason ? '' : ' (optional)'}</span>
                  <textarea className="input mt-1 min-h-[4.5rem]" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} />
                </label>
              )}
              <label className="block text-sm">
                <span className="font-medium text-havn-charcoal">Your name</span>
                <input className="input mt-1" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoComplete="name" required />
              </label>
            </div>

            <div className="mt-5 flex flex-col-reverse gap-2 border-t border-havn-cream-200 bg-havn-cream-50 px-5 py-4 sm:flex-row sm:justify-end">
              <Dialog.Close asChild>
                <button type="button" className="btn-secondary" disabled={busy}>Cancel</button>
              </Dialog.Close>
              <button type="submit" className={`${request?.danger ? 'btn-danger' : 'btn-primary'} disabled:cursor-not-allowed disabled:opacity-50`} disabled={!ready || busy}>
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                {request?.confirmLabel || 'Confirm'}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function Pair({ value }) {
  if (!value) return <span className="text-havn-gray-400">—</span>
  return <span className="tabular-nums">{value.glass}<span className="text-havn-gray-400"> / </span>{value.plastic}</span>
}

function Lines({ lines, muted }) {
  return (
    <ul className={`space-y-0.5 text-sm ${muted ? 'text-havn-gray-500' : 'text-havn-charcoal'}`}>
      {lines.map((l) => (
        <li key={l.id || `${l.itemId}-${l.customization || ''}`}>
          <span className="tabular-nums">{l.qty}×</span> {l.name}
          {l.customization && <span className="ml-1 text-havn-brass-600">· {l.customization}</span>}
        </li>
      ))}
    </ul>
  )
}

function WaitingCard({ order, lockOn, onDecide }) {
  const change = order.pendingChange
  const hasCustom = order.lines.some((l) => l.customization)
  return (
    <li className="rounded-xl border border-havn-cream-300 bg-white p-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium text-havn-charcoal">{order.customerName || order.id}</p>
        <span className="badge bg-havn-cream-200 text-havn-charcoal capitalize">{change ? change.container : order.container}</span>
        <span className="badge bg-amber-100 text-amber-800">{HOLD_LABEL[order.hold] || 'Waiting'}</span>
        {lockOn && order.fits === true && <span className="badge bg-emerald-100 text-emerald-800">Fits</span>}
        {lockOn && order.fits === false && <span className="badge bg-red-100 text-red-800">No room</span>}
        <span className={`badge ${order.paid ? 'bg-havn-green-700 text-white' : 'bg-havn-cream-200 text-havn-gray-600'}`}>{order.paid ? 'Paid' : 'Unpaid'}</span>
      </div>
      {change ? (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div><p className="mb-1 text-xs uppercase tracking-wider text-havn-gray-500">Admitted</p><Lines lines={order.lines} muted /></div>
          <div><p className="mb-1 text-xs uppercase tracking-wider text-havn-gray-500">Wants · {when(change.requestedAt)}</p><Lines lines={change.lines} /></div>
        </div>
      ) : (
        <div className="mt-3"><Lines lines={order.lines} /></div>
      )}
      {order.specialInstructions && <p className="mt-2 text-sm text-havn-gray-500">Note: {order.specialInstructions}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className="btn-primary" onClick={() => onDecide(order, 'approve')}>Approve</button>
        {!change && hasCustom && (
          <button type="button" className="btn-secondary" onClick={() => onDecide(order, 'approve_without_custom')}>Approve without customization</button>
        )}
        <button type="button" className="btn-ghost text-red-700 hover:text-red-800" onClick={() => onDecide(order, 'decline')}>Decline</button>
      </div>
    </li>
  )
}

export default function Plated() {
  const { currentCity, currentMenu } = useApp()
  const { success, error } = useToastActions()
  const [data, setData] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [request, setRequest] = useState(null)
  // The button that opened the pop-up gets focus back when it closes (the pop-up has no Radix Trigger).
  const opener = useRef(null)
  const ask = (next) => { opener.current = document.activeElement; setRequest(next) }
  const [busy, setBusy] = useState(false)
  const [platedDraft, setPlatedDraft] = useState({})
  const loadSeq = useRef(0)

  const supported = PLATED_CITIES.includes(currentCity)

  const load = useCallback(async () => {
    if (!supported || !currentMenu) return
    const seq = ++loadSeq.current
    try {
      const next = await platedAPI.get(currentCity, currentMenu)
      if (seq !== loadSeq.current) return
      setData(next)
      setLoadError(null)
    } catch (err) {
      if (seq === loadSeq.current) setLoadError(err.message)
    } finally {
      if (seq === loadSeq.current) setLoading(false)
    }
  }, [currentCity, currentMenu, supported])

  useEffect(() => { setData(null); setLoading(true); setPlatedDraft({}); load() }, [load])

  const refreshing = REFRESHING.has(data?.refresh?.status)
  useEffect(() => {
    if (!supported || !currentMenu) return undefined
    const id = setInterval(load, refreshing ? 4000 : 20000)
    return () => clearInterval(id)
  }, [load, refreshing, supported, currentMenu])

  const feed = data?.feed
  const lock = feed?.lock
  const dishes = useMemo(() => dishRows(feed), [feed])
  const waiting = useMemo(() => waitingOrders(feed), [feed])
  const inProduction = useMemo(() => (feed?.orders || []).filter((o) => o.kitchenStatus === 'in_production'), [feed])
  const flagged = inProduction.filter((o) => o.routingFlag)
  const lateAdds = inProduction.filter((o) => o.admittedAfterLock)

  const platedValue = (dish, container) => {
    const draft = platedDraft[dish.itemId]?.[container]
    if (draft !== undefined) return draft
    const base = dish.plated || dish.target
    return base ? String(base[container]) : ''
  }
  const platedChanges = dishes
    .map((d) => {
      const glass = Number(platedValue(d, 'glass') || 0)
      const plastic = Number(platedValue(d, 'plastic') || 0)
      const base = d.plated || d.target || { glass: 0, plastic: 0 }
      return { dish: d, glass, plastic, changed: platedDraft[d.itemId] && (glass !== base.glass || plastic !== base.plastic) }
    })
    .filter((c) => c.changed)
  const platedInvalid = Object.values(platedDraft).some((v) => Object.values(v).some((n) => n === '' || !/^\d+$/.test(n)))

  async function runAction(name, { input = {}, refresh = false, operator, done }) {
    setBusy(true)
    try {
      const out = await platedAPI.run(currentCity, name, { menuId: currentMenu, operator, input, refresh })
      success(done || out.envelope?.did || 'Saved')
      setRequest(null)
      await load()
      return true
    } catch (err) {
      error(err.message)
      return false
    } finally {
      setBusy(false)
    }
  }

  function askLock() {
    ask({
      title: lock ? 'Re-lock counts' : 'Lock counts',
      description: lock
        ? 'Replaces the cook target with what is in production now. Late orders are then measured against the new target.'
        : 'What is in production now becomes the cook target. After this, late orders with a customization or past the target wait here for approval.',
      rows: dishes.filter((d) => d.inProduction.total).map((d) => ({ area: d.name, detail: `${d.inProduction.glass} glass / ${d.inProduction.plastic} plastic` })),
      reason: lock ? 'required' : 'optional',
      reasonLabel: lock ? 'Why re-lock?' : 'Note',
      confirmLabel: lock ? 'Re-lock counts' : 'Lock counts',
      run: ({ operator, reason }) => runAction('lock_counts', { operator, input: reason ? { reason } : {}, done: lock ? 'Counts re-locked' : 'Counts locked' }),
    })
  }

  function askPlated() {
    ask({
      title: 'Save plated counts',
      description: 'From now on late orders are let in against what was plated, not the cook target.',
      rows: platedChanges.map((c) => ({ area: c.dish.name, detail: `${c.glass} glass / ${c.plastic} plastic` })),
      confirmLabel: 'Save plated counts',
      run: async ({ operator }) => {
        const ok = await runAction('set_plated_counts', {
          operator,
          input: { counts: platedChanges.map((c) => ({ item: c.dish.itemId, glass: c.glass, plastic: c.plastic })) },
          done: 'Plated counts saved',
        })
        if (ok) setPlatedDraft({})
      },
    })
  }

  function askAdmission(mode) {
    if (mode === feed?.admission) return
    ask({
      title: mode === 'manual' ? 'Approve every late order by hand?' : 'Let late orders in automatically?',
      description: mode === 'manual'
        ? 'After the lock, every late order waits here, even one that fits and has no customization.'
        : 'After the lock, a late order without a customization goes straight to production while it fits the plated count.',
      confirmLabel: mode === 'manual' ? 'Turn on manual approval' : 'Turn on automatic',
      run: ({ operator }) => runAction('set_admission', { operator, input: { mode }, done: mode === 'manual' ? 'Manual approval on' : 'Automatic admission on' }),
    })
  }

  function askDecide(order, choice) {
    const isDecline = choice === 'decline'
    const who = order.customerName || order.id
    ask({
      title: isDecline
        ? (order.pendingChange ? `Decline ${who}'s change?` : `Decline ${who}'s order?`)
        : (order.pendingChange ? `Apply ${who}'s change?` : choice === 'approve_without_custom' ? `Approve ${who} without the customization?` : `Approve ${who}'s order?`),
      description: isDecline
        ? 'The loop tells the customer. Nothing in the kitchen changes.'
        : 'Confirming updates these in the ops dashboard. Meal counts, customs and labels refresh from Havn OS in a minute or two.',
      rows: isDecline ? declineEffects(order) : approvalEffects(order, { withoutCustom: choice === 'approve_without_custom', routedOrders: feed?.totals?.routedOrders || 0 }),
      reason: isDecline ? 'optional' : null,
      reasonLabel: 'Reason (for the customer message)',
      danger: isDecline,
      confirmLabel: isDecline ? 'Decline' : 'Approve and update',
      run: ({ operator, reason }) => runAction('decide_order', {
        operator,
        input: { order: order.id, choice, ...(reason ? { reason } : {}) },
        refresh: !isDecline,
        done: isDecline ? `Declined ${who}` : `Approved ${who}; updating counts`,
      }),
    })
  }

  function askImport() {
    ask({
      title: 'Update meal counts?',
      description: 'Orders were let in since the last import. Confirming re-runs the import and updates meal counts, customs and labels.',
      rows: importEffects(data?.countsSinceImport),
      confirmLabel: 'Update counts',
      run: async ({ operator }) => {
        setBusy(true)
        try {
          await platedAPI.refresh(currentCity, { menuId: currentMenu, operator })
          success('Updating counts')
          setRequest(null)
          await load()
        } catch (err) {
          error(err.message)
        } finally {
          setBusy(false)
        }
      },
    })
  }

  if (!supported) {
    return <div className="card card-body text-sm text-havn-gray-600">The Plated tab is for Miami, Phoenix and Chicago, whose orders come from Havn OS.</div>
  }
  if (!currentMenu) {
    return <div className="card card-body text-sm text-havn-gray-600">Choose this week's menu in the sidebar.</div>
  }
  if (loading && !data) {
    return <div className="flex items-center gap-2 text-havn-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading Havn OS counts…</div>
  }
  if (!data) {
    return (
      <div className="card card-body flex items-start gap-3 text-sm">
        <AlertTriangle className="w-5 h-5 shrink-0 text-red-600" />
        <div>
          <p className="font-medium text-havn-charcoal">Havn OS counts could not be loaded.</p>
          <p className="mt-1 text-havn-gray-600">{loadError}</p>
          <button type="button" className="btn-secondary mt-3" onClick={() => { setLoading(true); load() }}><RefreshCw className="w-4 h-4" /> Try again</button>
        </div>
      </div>
    )
  }

  const refresh = data.refresh || {}
  const routed = feed.totals.routedOrders
  return (
    <div className="space-y-6">
      {loadError && (
        <p className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800"><AlertTriangle className="w-4 h-4" /> Showing the last good copy: {loadError}</p>
      )}

      {/* Lock status and admission */}
      <section className="card">
        <div className="card-body flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className={`rounded-lg p-2 ${lock ? 'bg-havn-green-700 text-white' : 'bg-havn-cream-200 text-havn-gray-500'}`}><Lock className="w-5 h-5" /></div>
            <div className="min-w-0">
              <p className="font-medium text-havn-charcoal">{lock ? 'Counts locked' : 'Counts not locked'}</p>
              <p className="text-sm text-havn-gray-500">
                {lock
                  ? `${lock.lockedBy} · ${when(lock.lockedAt)}${lock.version > 1 ? ` · re-lock ${lock.version - 1}` : ''}${lock.reason ? ` · ${lock.reason}` : ''}`
                  : 'Lock on Thursday: what is in production becomes the cook target.'}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div role="radiogroup" aria-label="Late orders after the lock" className="inline-flex rounded-lg bg-havn-cream-200 p-1 text-sm">
              {[['auto', 'Auto-admit'], ['manual', 'Approve all']].map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={feed.admission === mode}
                  onClick={() => askAdmission(mode)}
                  className={`rounded-md px-3 py-1.5 font-medium transition-colors ${feed.admission === mode ? 'bg-white text-havn-green-800 shadow-sm' : 'text-havn-gray-600 hover:text-havn-charcoal'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button type="button" className={lock ? 'btn-secondary' : 'btn-primary'} onClick={askLock}>
              <Lock className="w-4 h-4" /> {lock ? 'Re-lock' : 'Lock counts'}
            </button>
          </div>
        </div>
      </section>

      {/* Counts moved since the last import (orders let in automatically, or a refresh in progress) */}
      {(data.countsSinceImport.length > 0 || refresh.status === 'error' || refreshing || refresh.status === 'complete') && (
        <section className={`rounded-xl border px-4 py-3 text-sm ${refresh.status === 'error' ? 'border-red-200 bg-red-50' : 'border-havn-brass-300 bg-havn-cream-200'}`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="flex min-w-0 flex-1 items-start gap-2">
              {refreshing ? <Loader2 className="mt-0.5 w-4 h-4 shrink-0 animate-spin text-havn-brass-600" />
                : refresh.status === 'error' ? <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0 text-red-600" />
                : data.countsSinceImport.length ? <Clock className="mt-0.5 w-4 h-4 shrink-0 text-havn-brass-600" />
                : <CheckCircle2 className="mt-0.5 w-4 h-4 shrink-0 text-emerald-600" />}
              <p className="text-havn-charcoal">
                {refreshing ? 'Updating meal counts, customs and labels from Havn OS…'
                  : refresh.status === 'error' ? `Counts did not update: ${refresh.error}. Try again with Update counts.`
                  : data.countsSinceImport.length ? `${data.countsSinceImport.length} dish${data.countsSinceImport.length === 1 ? '' : 'es'} moved since the last import${data.lastImport ? ` (${when(data.lastImport.importedAt)})` : ''}.`
                  : `Meal counts updated ${when(refresh.completed_at)} by ${refresh.operator}.`}
              </p>
            </div>
            {!refreshing && (data.countsSinceImport.length > 0 || refresh.status === 'error') && (
              <button type="button" className="btn-primary shrink-0" onClick={askImport}><RefreshCw className="w-4 h-4" /> Update counts</button>
            )}
          </div>
        </section>
      )}

      {/* Waiting for approval */}
      <section>
        <h2 className="mb-3 flex items-center gap-2 font-display text-lg text-havn-green-900">
          Waiting for approval <span className="badge bg-havn-cream-200 font-sans tabular-nums text-havn-charcoal">{waiting.length}</span>
        </h2>
        {waiting.length ? (
          <ul className="grid gap-3 xl:grid-cols-2">
            {waiting.map((o) => <WaitingCard key={o.id} order={o} lockOn={Boolean(lock)} onDecide={askDecide} />)}
          </ul>
        ) : (
          <p className="card card-body text-sm text-havn-gray-500">{lock ? 'Nothing waiting. Late orders and changes land here.' : 'Nothing waits until the counts are locked.'}</p>
        )}
      </section>

      {/* Dishes: cook target, plated, in production */}
      <section className="card">
        <div className="card-header flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex-1">
            <h2 className="font-display text-lg text-havn-green-900">Dishes</h2>
            <p className="text-xs text-havn-gray-500">Glass / plastic. {lock?.platedAt ? `Plated entered by ${lock.platedBy} · ${when(lock.platedAt)}` : 'Enter plated counts after plating; they become the limit.'}</p>
          </div>
          {lock && (
            <button type="button" className="btn-primary" disabled={!platedChanges.length || platedInvalid} onClick={askPlated}>Save plated counts</button>
          )}
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr><th>Dish</th><th>Cook target</th><th>Plated</th><th>In production</th><th>Waiting</th></tr>
            </thead>
            <tbody>
              {dishes.map((d) => {
                const limit = d.limit
                const over = limit && (d.inProduction.glass > limit.glass || d.inProduction.plastic > limit.plastic)
                return (
                  <tr key={d.itemId}>
                    <td className="font-medium text-havn-charcoal">{d.name}</td>
                    <td><Pair value={d.target} /></td>
                    <td>
                      {lock ? (
                        <div className="flex items-center gap-1">
                          {['glass', 'plastic'].map((c, i) => (
                            <span key={c} className="flex items-center gap-1">
                              {i === 1 && <span className="text-havn-gray-400">/</span>}
                              <input
                                className="input w-16 px-2 py-1.5 text-center tabular-nums"
                                inputMode="numeric"
                                aria-label={`${d.name} plated ${c}`}
                                value={platedValue(d, c)}
                                onChange={(e) => setPlatedDraft((prev) => ({ ...prev, [d.itemId]: { ...prev[d.itemId], [c]: e.target.value.trim() } }))}
                              />
                            </span>
                          ))}
                        </div>
                      ) : <Pair value={null} />}
                    </td>
                    <td className={over ? 'font-medium text-red-700' : ''}><Pair value={d.inProduction} /></td>
                    <td>{d.pending.total ? <Pair value={d.pending} /> : <span className="text-havn-gray-400">—</span>}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Routing record */}
      <section className="card">
        <div className="card-header flex items-center gap-2">
          <Truck className="w-5 h-5 text-havn-brass-600" />
          <h2 className="flex-1 font-display text-lg text-havn-green-900">Routes</h2>
          <span className="text-sm text-havn-gray-500">{routed ? `${routed} of ${inProduction.length} orders routed` : 'Not built yet'}</span>
        </div>
        <div className="card-body space-y-4 text-sm">
          {flagged.length > 0 ? (
            <ul className="divide-y divide-havn-cream-200">
              {flagged.map((o) => (
                <li key={o.id} className="flex flex-wrap items-center gap-2 py-2">
                  <MapPin className="w-4 h-4 text-havn-gray-400" />
                  <span className="font-medium text-havn-charcoal">{o.customerName || o.id}</span>
                  {o.routingFlag === 'not_routed'
                    ? <span className="badge bg-amber-100 text-amber-800">Needs a stop</span>
                    : o.pendingChange
                      ? <span className="badge bg-amber-100 text-amber-800">Change waiting</span>
                      : <span className="badge bg-red-100 text-red-800">Changed after routing</span>}
                  {o.routing && <span className="text-havn-gray-500">{o.routing.route} · stop {o.routing.stop}{o.routing.driver ? ` · ${o.routing.driver}` : ''}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-havn-gray-500">{routed ? 'Every order in production has a stop and none changed since.' : 'Once the driver app builds routes, each order shows its route and stop here.'}</p>
          )}
          {lateAdds.length > 0 && (
            <div>
              <p className="mb-1 text-xs uppercase tracking-wider text-havn-gray-500">Added after the lock ({lateAdds.length})</p>
              <p className="text-havn-charcoal">{lateAdds.map((o) => o.customerName || o.id).join(', ')}</p>
            </div>
          )}
        </div>
      </section>

      <UpdateDialog request={request} busy={busy} returnFocus={opener} onClose={() => setRequest(null)} onConfirm={(args) => request?.run(args)} />
    </div>
  )
}
